import { useState, useRef, useEffect } from 'react';
import {
  Stack,
  Group,
  Paper,
  Title,
  Text,
  TextInput,
  ScrollArea,
  Badge,
  ActionIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSend, IconRobot, IconUser } from '@tabler/icons-react';
import { usePayrollAIAssistant } from '@/hooks/usePortalApi';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const SUGGESTED_QUESTIONS = [
  'Why was my last check different?',
  'What are my deductions?',
  'How much tax was withheld?',
  'What is my YTD gross pay?',
  'When is the next pay date?',
];

export default function PayrollAIAssistant() {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hi! I\'m your Pay Assistant. I can help with questions about your pay stubs, deductions, and tax withholdings. What would you like to know?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const viewport = useRef<HTMLDivElement>(null);
  const aiMutation = usePayrollAIAssistant();

  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const handleSend = (question?: string) => {
    const text = question || input.trim();
    if (!text) return;

    const userMsg: Message = { role: 'user', content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    aiMutation.mutate(
      { question: text },
      {
        onSuccess: (data) => {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: data.answer ?? 'I could not find an answer to your question.',
              timestamp: new Date(),
            },
          ]);
        },
        onError: () => {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: 'Sorry, I encountered an error. Please try again.',
              timestamp: new Date(),
            },
          ]);
          notifications.show({ title: 'Error', message: 'Failed to get response', color: 'red' });
        },
      }
    );
  };

  return (
    <Stack gap="lg">
      <Group gap="xs">
        <IconRobot size={24} />
        <Title order={3}>Pay Assistant</Title>
      </Group>

      <Paper p="md" shadow="sm" style={{ height: 500, display: 'flex', flexDirection: 'column' }}>
        <ScrollArea flex={1} viewportRef={viewport} mb="md">
          <Stack gap="sm" p="xs">
            {messages.map((msg, idx) => (
              <Group
                key={idx}
                justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                align="flex-start"
                gap="xs"
              >
                {msg.role === 'assistant' && (
                  <IconRobot size={20} style={{ marginTop: 4, color: 'var(--mantine-color-blue-6)' }} />
                )}
                <Paper
                  p="sm"
                  radius="md"
                  bg={msg.role === 'user' ? 'blue.1' : 'gray.0'}
                  maw="70%"
                >
                  <Text size="sm">{msg.content}</Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    {msg.timestamp.toLocaleTimeString()}
                  </Text>
                </Paper>
                {msg.role === 'user' && (
                  <IconUser size={20} style={{ marginTop: 4, color: 'var(--mantine-color-gray-6)' }} />
                )}
              </Group>
            ))}
            {aiMutation.isPending && (
              <Group gap="xs">
                <IconRobot size={20} style={{ color: 'var(--mantine-color-blue-6)' }} />
                <Text size="sm" c="dimmed" fs="italic">Thinking...</Text>
              </Group>
            )}
          </Stack>
        </ScrollArea>

        <Group gap="xs" mb="sm" wrap="wrap">
          {SUGGESTED_QUESTIONS.map((q) => (
            <Badge
              key={q}
              variant="outline"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSend(q)}
            >
              {q}
            </Badge>
          ))}
        </Group>

        <Group gap="xs">
          <TextInput
            placeholder="Ask about your pay..."
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            style={{ flex: 1 }}
            disabled={aiMutation.isPending}
          />
          <ActionIcon
            size="lg"
            color="blue"
            onClick={() => handleSend()}
            loading={aiMutation.isPending}
          >
            <IconSend size={18} />
          </ActionIcon>
        </Group>
      </Paper>
    </Stack>
  );
}
